import { Component, h } from "preact";
import {
  IEdges,
  IImageMeta,
  IImageRendererProps,
  IImageRendererState,
} from "../../../typings";
import EventController from "../../../shared/controller/event-controller";
import { ISize } from "../../../shared/controller/size-controller";
import cropperjs from "cropperjs";
import Renderer from "../../../canvas/Renderer";

export default class ImageRenderer extends Component<
  IImageRendererProps,
  IImageRendererState
> {
  private image: HTMLImageElement;
  private span: HTMLSpanElement;
  private renderer: Renderer;

  constructor(props: IImageRendererProps) {
    super(props);

    this.state = {
      edges: {
        top: false,
        right: false,
        bottom: false,
        left: false,
      },
      labelSize: {
        width: 0,
        height: 0,
      },
    };

    this.renderer = new Renderer();

    this.loadEventListeners = this.loadEventListeners.bind(this);
  }

  componentDidMount(): void {
    const state: any = {};
    state.labelSize = this.props.sizeController.getSize();

    const image: string = this.props.outputController.getValue([
      "image",
      this.props.originalPngName,
    ]);
    const imageData: cropperjs.Data = this.props.outputController.getValue([
      "image",
      this.props.croppedDataName,
    ]);
    // eslint-disable-next-line no-console
    console.log("ImageRenderer is rendered", image);
    if (image) {
      this.loadOriginalPng(image);
    }

    const edgesIncluded: boolean = this.props.outputController.getValue([
      "label",
      "edges_includes",
    ]);
    state.edgesIncluded = edgesIncluded;
    state.edges = {};
    const edges: IEdges = this.props.outputController.getValue([
      "label",
      "edges",
    ]);
    if (edges) {
      state.edges = edges;
    }

    state.sewingAllowance = this.props.outputController.getValue([
      "label",
      "edge",
    ]);

    state.fold = this.props.outputController.getValue(["label", "fold"]);

    this.loadEventListeners();

    this.setState(state);
  }

  private loadOriginalPng(image: string): void {
    const imageEl: HTMLImageElement = new Image();
    imageEl.addEventListener("load", () => {
      this.setState({ image: imageEl });
    });
    imageEl.addEventListener("error", () => {
      const imageOriginal: string = this.props.outputController.getValue([
        "image",
        this.props.originalName,
      ]);
      const metaData: IImageMeta = this.props.outputController.getValue([
        "image",
        this.props.originalMetaName,
      ]);
      this.props.externalController
        .convertImage(imageOriginal, metaData.name)
        .then((pngUrl: string) => {
          this.props.outputController.setValue(
            ["image", this.props.originalPngName],
            pngUrl,
          );
          this.loadOriginalPng(pngUrl);
        })
        .catch((e: any) => {
          this.props.externalController.main.setMessage(
            this.props.externalController.main.templateController.getTemplate()
              .meta.fields.errorFileCouldNotBeUploaded,
          );
          throw e;
        });
    });
    imageEl.crossOrigin = "anonymous";
    imageEl.src = image;
  }

  loadEventListeners(): void {
    let eventName: string = EventController.getSaveEventName([
      "image",
      this.props.croppedDataName,
    ]);
    document.body
      .querySelector(this.props.conf.target)
      .addEventListener(eventName, () => {
        const value: string = this.props.outputController.getValue([
          "image",
          this.props.originalPngName,
        ]);
        const image: HTMLImageElement = new Image();
        image.addEventListener("load", () => {
          this.setState({
            image,
          });
        });
        image.crossOrigin = "anonymous";
        image.src = value;
      });

    eventName = EventController.getSaveEventName([
      "image",
      this.props.originalPngName,
    ]);
    document.body
      .querySelector(this.props.conf.target)
      .addEventListener(eventName, () => {
        const value: string = this.props.outputController.getValue([
          "image",
          this.props.originalPngName,
        ]);
        const image: HTMLImageElement = new Image();
        image.addEventListener("load", () => {
          this.setState({
            image,
          });
        });
        image.crossOrigin = "anonymous";
        image.src = value;
      });

    eventName = EventController.getSaveEventName(["label", "edges"]);
    document.body
      .querySelector(this.props.conf.target)
      .addEventListener(eventName, () => {
        const edges: IEdges = this.props.outputController.getValue([
          "label",
          "edges",
        ]);
        this.setState({ edges: edges ? edges : {} });
      });

    eventName = EventController.getSaveEventName(["label", "fold"]);
    document.body
      .querySelector(this.props.conf.target)
      .addEventListener(eventName, () => {
        const fold: "horizontal" | "vertical" =
          this.props.outputController.getValue(["label", "fold"]);
        this.setState({ fold });
      });

    eventName = EventController.getSaveEventName(["label", "edge"]);
    document.body
      .querySelector(this.props.conf.target)
      .addEventListener(eventName, () => {
        const edge: "horizontal" | "vertical" | void =
          this.props.outputController.getValue(["label", "edge"]);
        this.setState({ sewingAllowance: edge });
      });

    eventName = EventController.getSaveEventName(["label", "edges_includes"]);
    document.body
      .querySelector(this.props.conf.target)
      .addEventListener(eventName, () => {
        const edgesIncluded: boolean = this.props.outputController.getValue([
          "label",
          "edges_includes",
        ]);
        const edges: IEdges = this.props.outputController.getValue([
          "label",
          "edges",
        ]);
        this.setState({ edgesIncluded, edges: edges ? edges : {} });
      });

    eventName = EventController.getSaveEventName(["size", "width"]);
    document.body
      .querySelector(this.props.conf.target)
      .addEventListener(eventName, () => {
        const size: ISize = this.props.sizeController.getSize();
        this.setState({
          labelSize: size,
        });
      });

    eventName = EventController.getSaveEventName(["size", "height"]);
    document.body
      .querySelector(this.props.conf.target)
      .addEventListener(eventName, () => {
        const size: ISize = this.props.sizeController.getSize();
        this.setState({
          labelSize: size,
        });
      });

    if (this.props.renderOnDemandEventName) {
      document.body
        .querySelector(this.props.conf.target)
        .addEventListener(this.props.renderOnDemandEventName, () => {
          this.renderCanvas(this.state.canvas);
        });
    }
  }

  private shouldRenderCanvas(): boolean {
    const meta: IImageMeta = this.props.outputController.getValue([
      "image",
      this.props.originalMetaName,
    ]);
    return !(!meta || !this.state.labelSize || !this.state.image);
  }

  render(): JSX.Element {
    if (!this.shouldRenderCanvas()) {
      return <span />;
    }
    this.renderer.preset(this.props, this.state);

    const width: any =
      typeof this.props.width === "number"
        ? this.renderer.getCanvasWidth()
        : "100%";
    const height: number =
      typeof this.props.height === "number"
        ? this.renderer.getCanvasHeight()
        : 0;

    return (
      <canvas
        className="shadow-language"
        style={{ width, height }}
        ref={(canvas: HTMLCanvasElement) => {
          if (!canvas) {
            return;
          }
          if (this.props.canvasRef) {
            this.props.canvasRef(canvas);
          }
          if (this.state.canvas !== canvas) {
            this.setState({ canvas }, () => {
              if (this.props.renderedOnDemandEventName) {
                return;
              }
              this.renderCanvas(canvas);
            });
          } else {
            if (this.props.renderedOnDemandEventName) {
              return;
            }
            this.renderCanvas(canvas);
          }
        }}
      />
    );
  }

  private renderCanvas(canvas: HTMLCanvasElement): void {
    this.renderer.preset(this.props, this.state);
    this.renderer.renderCanvas(canvas);

    if (!this.props.renderedOnDemandEventName) {
      return;
    }

    document.body.querySelector(this.props.conf.target).dispatchEvent(
      new CustomEvent(this.props.renderedOnDemandEventName, {
        detail: canvas.toDataURL(),
      }),
    );
  }
}
